import { PButton } from "@porsche-design-system/components-react"
import type {
	InventoryInformationChange,
	InventoryInformationEditView,
	InventoryInformationView,
	ModelInformationView,
} from "@slfinrtl/ui-bff-routes"
import { updateInventoryInformationRoute } from "@slfinrtl/ui-bff-routes"
import { dequal } from "dequal/lite"
import { useCallback, useEffect, useState } from "react"
import { FormProvider, useForm, useFormContext } from "react-hook-form"
import { FormattedMessage } from "react-intl"
import { formMessages } from "src/common/form.messages"
import { getDifference } from "src/common/getDifference/getDifference"
import { transformValues } from "src/common/transformValues/transformValues"
import type { InventoryInformationFormValues } from "src/components"
import {
	ButtonGroup,
	ConditionAndServiceForm,
	conditionFieldNames,
	getInventoryInformationChangeRules,
	mapToInventoryInformationChange,
	mapToInventoryInformationFormValues,
	useConditionAndServiceFields,
} from "src/components"
import { useBffDynamic, useConfig } from "src/hooks"
import { useVehicleDetailsContext } from "src/screens/vehicleDetails/context/VehicleDetailsContext"

interface ConditionAndServiceFormContainerProps {
	editView: InventoryInformationEditView
	inventoryId: string
	onCancel: () => void
	onSaved: (view: InventoryInformationView) => void
}

export const ConditionAndServiceFormContainer = ({ editView, ...props }: ConditionAndServiceFormContainerProps) => {
	const form = useForm<InventoryInformationFormValues>({
		defaultValues: mapToInventoryInformationFormValues(editView.values),
		mode: "onChange",
	})

	return (
		<FormProvider {...form}>
			<ConditionAndServiceInnerFormContainer editView={editView} {...props} />
		</FormProvider>
	)
}

type ConditionAndServiceInnerFormContainerProps = ConditionAndServiceFormContainerProps

const ConditionAndServiceInnerFormContainer = ({
	editView,
	inventoryId,
	onCancel,
	onSaved,
}: ConditionAndServiceInnerFormContainerProps) => {
	const [isSaving, setIsSaving] = useState(false)
	const [modelInformationView, setModelInformationView] = useState<ModelInformationView | null>()

	const { subscribe } = useVehicleDetailsContext()

	useEffect(() => subscribe("modelInformationChanged", setModelInformationView), [subscribe])

	const config = useConfig()
	const { handleSubmit, reset, watch } = useFormContext<InventoryInformationFormValues>()

	const values = watch()
	const fields = useConditionAndServiceFields(false, modelInformationView?.modelYear, editView.isElectric)

	const patch = useBffDynamic(updateInventoryInformationRoute, "inline")

	const onValid = () => {
		const transformedValues = transformValues<InventoryInformationFormValues>(
			getInventoryInformationChangeRules(config),
			values,
			config,
		)
		const informationChange = mapToInventoryInformationChange(transformedValues)
		if (informationChange) {
			save(informationChange)
		}
	}

	const save = useCallback(
		(informationChange: InventoryInformationChange) => {
			const patchedValues = informationChange ? getDifference(informationChange, editView.values) : undefined

			setIsSaving(true)
			return patch({
				body: patchedValues
					? {
							...patchedValues,
							pricing: {
								...patchedValues.pricing,
								// PricingUseCase must always be sent to the
								// bff
								pricingUseCase: editView.values.pricing.pricingUseCase,
							},
						}
					: {},
				pathParams: { inventoryId },
				queryParams: {},
			})
				.then(onSaved)
				.finally(() => setIsSaving(false))
		},
		[inventoryId, onSaved, patch, editView.values],
	)

	useEffect(() => {
		const transformedValues = transformValues(getInventoryInformationChangeRules(config), values, config)

		// Reset only condition values. Leave other stages as they are for now,
		// to make sure that changing a value and then reverting it leads back to the original state.
		const newValues: InventoryInformationFormValues = { ...values }
		Object.values(conditionFieldNames).forEach((fieldName) => {
			;(newValues[fieldName] as unknown) = transformedValues[fieldName]
		})

		if (!dequal(newValues, values)) {
			reset(newValues, { keepErrors: true, keepFieldsRef: true })
		}
	}, [reset, config, values])

	return (
		<>
			<ConditionAndServiceForm fields={fields} />
			<ButtonGroup>
				<PButton loading={isSaving} onClick={handleSubmit(onValid)}>
					<FormattedMessage {...formMessages.save} />
				</PButton>
				<PButton type="button" variant="secondary" onClick={onCancel}>
					<FormattedMessage {...formMessages.cancel} />
				</PButton>
			</ButtonGroup>
		</>
	)
}
